<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import type { IProduct, IApiResponseProduct } from '@/types/product.types';

const route = useRoute();
const router = useRouter();

const commerceId = computed(() => (route.query.commerceId as string) ?? '');

type PromoTrigger =
  'automatic' | 'code_required' | 'first_time_customer' | 'customer_segment';
type PromoType =
  | 'percentage'
  | 'fixed_amount'
  | 'buy_one_get_one'
  | 'quantity_discount'
  | 'bundle_discount';
type PromoStatus = 'draft' | 'active' | 'paused';
type RuleType =
  | 'product_in_cart'
  | 'category_in_cart'
  | 'cart_total'
  | 'customer_group'
  | 'customer_tag'
  | 'quantity_threshold'
  | 'day_of_week'
  | 'time_of_day'
  | 'payment_method';
type RuleOp =
  | 'equals'
  | 'not_equals'
  | 'greater_than'
  | 'less_than'
  | 'contains'
  | 'in'
  | 'not_in';

interface Rule {
  type: RuleType;
  operator: RuleOp;
  value: any;
}

// ── Chargement produits du commerce ──────────────────────────────────────
const { data: productsResp } = useQuery({
  queryKey: computed(() => ['promo-products', commerceId.value]),
  queryFn: async () => {
    const params = new URLSearchParams({
      restau: commerceId.value,
      pagination: 'false',
    });
    const r = await axiosInstance.get<IApiResponseProduct>(
      `products/search?${params}`,
    );
    const d = r.data as any;
    return (d?.data?.data ?? d?.data ?? []) as IProduct[];
  },
  enabled: computed(() => !!commerceId.value),
  staleTime: 60_000,
});
const allProducts = computed<IProduct[]>(() => productsResp.value ?? []);
const allCategories = computed(() => {
  const seen = new Map<string, { id: string; name: string }>();
  for (const p of allProducts.value) {
    for (const pc of p.categories ?? []) {
      const cat = (pc as any).category ?? pc;
      const cid = cat.id ?? (pc as any).id;
      if (cid && !seen.has(cid))
        seen.set(cid, { id: cid, name: cat.designation ?? cat.name ?? cid });
    }
  }
  return [...seen.values()];
});

// ── Formulaire ────────────────────────────────────────────────────────────
const form = ref({
  name: '',
  description: '',
  type: 'percentage' as PromoType,
  trigger: 'automatic' as PromoTrigger,
  status: 'active' as PromoStatus,
  value: 10,
  code: '',
  minimumOrderAmount: 0,
  maximumDiscountAmount: 0,
  usageLimit: 0,
  usageLimitPerCustomer: 0,
  priority: 0,
  stackable: false,
  startDate: '',
  endDate: '',
});

const selectedProductIds = ref<Set<string>>(new Set());
const selectedCategoryIds = ref<Set<string>>(new Set());
const rules = ref<Rule[]>([]);

const isCodeTrigger = computed(() => form.value.trigger === 'code_required');

// reset code si trigger change
watch(
  () => form.value.trigger,
  (t) => {
    if (t !== 'code_required') form.value.code = '';
  },
);

// ── Produits / catégories — sélecteurs ───────────────────────────────────
const prodSearch = ref('');
const catSearch = ref('');

const filteredProds = computed(() => {
  const q = prodSearch.value.trim().toLowerCase();
  return allProducts.value.filter(
    (p) => !p.disabledAt && (!q || p.designation.toLowerCase().includes(q)),
  );
});
const filteredCats = computed(() => {
  const q = catSearch.value.trim().toLowerCase();
  return allCategories.value.filter(
    (c) => !q || c.name.toLowerCase().includes(q),
  );
});

function toggleProd(id: string) {
  const s = new Set(selectedProductIds.value);
  s.has(id) ? s.delete(id) : s.add(id);
  selectedProductIds.value = s;
}
function toggleCat(id: string) {
  const s = new Set(selectedCategoryIds.value);
  s.has(id) ? s.delete(id) : s.add(id);
  selectedCategoryIds.value = s;
}

// ── Règles ────────────────────────────────────────────────────────────────
const RULE_TYPES: { v: RuleType; label: string }[] = [
  { v: 'product_in_cart', label: 'Produit dans le panier' },
  { v: 'category_in_cart', label: 'Catégorie dans le panier' },
  { v: 'cart_total', label: 'Total panier' },
  { v: 'customer_group', label: 'Groupe client' },
  { v: 'customer_tag', label: 'Tag client' },
  { v: 'quantity_threshold', label: 'Seuil de quantité' },
  { v: 'day_of_week', label: 'Jour de la semaine' },
  { v: 'time_of_day', label: 'Heure de la journée' },
  { v: 'payment_method', label: 'Moyen de paiement' },
];
const RULE_OPS: { v: RuleOp; label: string }[] = [
  { v: 'equals', label: '=' },
  { v: 'not_equals', label: '≠' },
  { v: 'greater_than', label: '>' },
  { v: 'less_than', label: '<' },
  { v: 'contains', label: 'contient' },
  { v: 'in', label: 'dans' },
  { v: 'not_in', label: 'pas dans' },
];

function addRule() {
  rules.value.push({ type: 'cart_total', operator: 'greater_than', value: '' });
}
function removeRule(i: number) {
  rules.value.splice(i, 1);
}

// ── Soumission ────────────────────────────────────────────────────────────
const saving = ref(false);
const error = ref('');

async function submit() {
  error.value = '';
  if (!form.value.name.trim()) {
    error.value = 'Le nom est obligatoire.';
    return;
  }
  saving.value = true;
  try {
    const body: Record<string, any> = {
      restaurantId: commerceId.value,
      name: form.value.name.trim(),
      type: form.value.type,
      trigger: form.value.trigger,
      status: form.value.status,
      value: form.value.value,
    };
    if (form.value.description.trim())
      body.description = form.value.description.trim();
    if (isCodeTrigger.value && form.value.code)
      body.code = form.value.code.trim().toUpperCase();
    if (form.value.minimumOrderAmount > 0)
      body.minimumOrderAmount = form.value.minimumOrderAmount;
    if (form.value.maximumDiscountAmount > 0)
      body.maximumDiscountAmount = form.value.maximumDiscountAmount;
    if (form.value.usageLimit > 0) body.usageLimit = form.value.usageLimit;
    if (form.value.usageLimitPerCustomer > 0)
      body.usageLimitPerCustomer = form.value.usageLimitPerCustomer;
    if (form.value.priority > 0) body.priority = form.value.priority;
    if (form.value.stackable) body.stackable = true;
    if (form.value.startDate) body.startDate = form.value.startDate;
    if (form.value.endDate) body.endDate = form.value.endDate;
    if (selectedProductIds.value.size)
      body.productIds = [...selectedProductIds.value];
    if (selectedCategoryIds.value.size)
      body.categoryIds = [...selectedCategoryIds.value];
    const validRules = rules.value.filter(
      (r) => r.value !== '' && r.value !== null,
    );
    if (validRules.length)
      body.rules = validRules.map((r) => ({
        type: r.type,
        operator: r.operator,
        value: r.value,
      }));

    await axiosInstance.post('promotions/create', body);
    goBack();
  } catch (e: any) {
    const msg = e?.response?.data?.message;
    error.value = Array.isArray(msg)
      ? msg.join('\n')
      : (msg ?? 'Une erreur est survenue.');
  } finally {
    saving.value = false;
  }
}

function goBack() {
  if (commerceId.value) router.push(`/commerces/${commerceId.value}`);
  else router.back();
}
</script>

<template>
  <div class="cpp__page">
    <!-- Header sticky -->
    <div class="cpp__header">
      <button class="cpp__back" @click="goBack">
        <i class="pi pi-arrow-left" />
      </button>
      <div class="cpp__header-info">
        <h1 class="cpp__title">Nouvelle promotion</h1>
        <p class="cpp__sub">Commerce {{ commerceId || '—' }}</p>
      </div>
      <div class="cpp__header-actions">
        <button class="cpp__btn cpp__btn--ghost" @click="goBack">
          Annuler
        </button>
        <button
          class="cpp__btn cpp__btn--primary"
          :disabled="saving"
          @click="submit"
        >
          <i v-if="saving" class="pi pi-spinner pi-spin" />
          {{ saving ? 'Enregistrement…' : 'Créer la promotion' }}
        </button>
      </div>
    </div>

    <!-- Body -->
    <div class="cpp__body">
      <!-- ── Colonne principale ── -->
      <div class="cpp__main">
        <!-- Identité -->
        <div class="cpp__card">
          <h2 class="cpp__card-title"><i class="pi pi-tag" /> Identité</h2>
          <div class="cpp__fields">
            <div class="cpp__field cpp__field--full">
              <label class="cpp__label"
                >Nom <span class="cpp__req">*</span></label
              >
              <input
                v-model="form.name"
                class="cpp__input"
                placeholder="Ex : -10% weekend, Offre Ramadan…"
              />
            </div>
            <div class="cpp__field cpp__field--full">
              <label class="cpp__label">Description</label>
              <textarea
                v-model="form.description"
                class="cpp__input cpp__textarea"
                rows="2"
                placeholder="Description courte (optionnel)"
              />
            </div>
          </div>
        </div>

        <!-- Remise -->
        <div class="cpp__card">
          <h2 class="cpp__card-title"><i class="pi pi-percentage" /> Remise</h2>
          <div class="cpp__fields">
            <div class="cpp__field">
              <label class="cpp__label">Type</label>
              <select v-model="form.type" class="cpp__select">
                <option value="percentage">Pourcentage (%)</option>
                <option value="fixed_amount">Montant fixe (€)</option>
                <option value="buy_one_get_one">1 acheté = 1 offert</option>
                <option value="quantity_discount">Remise quantité</option>
                <option value="bundle_discount">Bundle</option>
              </select>
            </div>
            <div class="cpp__field">
              <label class="cpp__label">Valeur</label>
              <div class="cpp__input-wrap">
                <input
                  v-model.number="form.value"
                  type="number"
                  min="0"
                  step="0.01"
                  class="cpp__input cpp__input--sym"
                />
                <span class="cpp__sym">{{
                  form.type === 'percentage' ? '%' : '€'
                }}</span>
              </div>
            </div>
            <div class="cpp__field">
              <label class="cpp__label">Remise max (€)</label>
              <input
                v-model.number="form.maximumDiscountAmount"
                type="number"
                min="0"
                step="0.01"
                class="cpp__input"
                placeholder="0 = illimité"
              />
            </div>
            <div class="cpp__field">
              <label class="cpp__label">Montant min de commande (€)</label>
              <input
                v-model.number="form.minimumOrderAmount"
                type="number"
                min="0"
                step="0.01"
                class="cpp__input"
                placeholder="0 = sans minimum"
              />
            </div>
          </div>
        </div>

        <!-- Déclencheur -->
        <div class="cpp__card">
          <h2 class="cpp__card-title">
            <i class="pi pi-bolt" /> Déclenchement
          </h2>
          <div class="cpp__trigger-grid">
            <button
              v-for="opt in [
                {
                  v: 'automatic',
                  icon: 'pi-bolt',
                  label: 'Automatique',
                  desc: 'Appliqué auto si conditions remplies',
                },
                {
                  v: 'code_required',
                  icon: 'pi-ticket',
                  label: 'Code requis',
                  desc: 'Le client saisit un code promo',
                },
                {
                  v: 'first_time_customer',
                  icon: 'pi-star',
                  label: '1er achat',
                  desc: 'Nouveaux clients uniquement',
                },
                {
                  v: 'customer_segment',
                  icon: 'pi-users',
                  label: 'Segment client',
                  desc: 'Par groupe ou segment',
                },
              ]"
              :key="opt.v"
              class="cpp__trigger-btn"
              :class="{ 'cpp__trigger-btn--on': form.trigger === opt.v }"
              @click="form.trigger = opt.v as PromoTrigger"
            >
              <i :class="`pi ${opt.icon}`" class="cpp__trigger-ico" />
              <span class="cpp__trigger-label">{{ opt.label }}</span>
              <span class="cpp__trigger-desc">{{ opt.desc }}</span>
            </button>
          </div>
          <div
            v-if="isCodeTrigger"
            class="cpp__field cpp__field--full"
            style="margin-top: 1rem"
          >
            <label class="cpp__label">Code promo</label>
            <input
              v-model="form.code"
              class="cpp__input cpp__input--code"
              placeholder="EX : SUMMER20"
              @input="form.code = (form.code as string).toUpperCase()"
            />
          </div>
        </div>

        <!-- Produits ciblés -->
        <div class="cpp__card">
          <h2 class="cpp__card-title">
            <i class="pi pi-box" /> Produits ciblés
            <span class="cpp__card-badge" v-if="selectedProductIds.size">{{
              selectedProductIds.size
            }}</span>
          </h2>
          <p class="cpp__card-hint">
            Laisse vide pour appliquer à tous les produits.
          </p>
          <div class="cpp__search-wrap">
            <i class="pi pi-search cpp__search-ico" />
            <input
              v-model="prodSearch"
              class="cpp__input cpp__search-input"
              placeholder="Rechercher un produit…"
            />
          </div>
          <div class="cpp__picker-list">
            <div
              v-for="p in filteredProds"
              :key="p.id"
              class="cpp__picker-row"
              :class="{ 'cpp__picker-row--on': selectedProductIds.has(p.id) }"
              @click="toggleProd(p.id)"
            >
              <div class="cpp__picker-ico">
                <img
                  v-if="p.image?.info?.secureUrl"
                  :src="p.image.info.secureUrl"
                  class="cpp__picker-img"
                />
                <i v-else class="pi pi-box" />
              </div>
              <span class="cpp__picker-name">{{ p.designation }}</span>
              <span class="cpp__picker-price"
                >{{ parseFloat(p.price as any).toFixed(2) }} €</span
              >
              <i
                class="pi cpp__picker-check"
                :class="
                  selectedProductIds.has(p.id) ? 'pi-check-circle' : 'pi-circle'
                "
              />
            </div>
            <p v-if="filteredProds.length === 0" class="cpp__picker-empty">
              Aucun produit trouvé.
            </p>
          </div>
        </div>

        <!-- Catégories ciblées -->
        <div class="cpp__card">
          <h2 class="cpp__card-title">
            <i class="pi pi-folder" /> Catégories ciblées
            <span class="cpp__card-badge" v-if="selectedCategoryIds.size">{{
              selectedCategoryIds.size
            }}</span>
          </h2>
          <p class="cpp__card-hint">
            Laisse vide pour appliquer à toutes les catégories.
          </p>
          <div class="cpp__search-wrap">
            <i class="pi pi-search cpp__search-ico" />
            <input
              v-model="catSearch"
              class="cpp__input cpp__search-input"
              placeholder="Rechercher une catégorie…"
            />
          </div>
          <div class="cpp__picker-list cpp__picker-list--cats">
            <div
              v-for="c in filteredCats"
              :key="c.id"
              class="cpp__picker-row"
              :class="{ 'cpp__picker-row--on': selectedCategoryIds.has(c.id) }"
              @click="toggleCat(c.id)"
            >
              <div class="cpp__picker-ico"><i class="pi pi-tag" /></div>
              <span class="cpp__picker-name">{{ c.name }}</span>
              <i
                class="pi cpp__picker-check"
                :class="
                  selectedCategoryIds.has(c.id)
                    ? 'pi-check-circle'
                    : 'pi-circle'
                "
              />
            </div>
            <p v-if="filteredCats.length === 0" class="cpp__picker-empty">
              Aucune catégorie trouvée.
            </p>
          </div>
        </div>

        <!-- Règles de condition -->
        <div class="cpp__card">
          <div class="cpp__card-hd">
            <h2 class="cpp__card-title" style="margin: 0">
              <i class="pi pi-sliders-h" /> Règles de condition
            </h2>
            <button class="cpp__add-rule-btn" @click="addRule">
              <i class="pi pi-plus" /> Ajouter
            </button>
          </div>
          <p class="cpp__card-hint">
            Conditions supplémentaires à remplir pour déclencher la promotion.
          </p>

          <div v-if="rules.length === 0" class="cpp__rules-empty">
            <i class="pi pi-sliders-h" /> Aucune règle — promotion appliquée
            sans condition supplémentaire.
          </div>

          <div v-for="(rule, i) in rules" :key="i" class="cpp__rule-row">
            <select v-model="rule.type" class="cpp__select cpp__rule-select">
              <option v-for="rt in RULE_TYPES" :key="rt.v" :value="rt.v">
                {{ rt.label }}
              </option>
            </select>
            <select v-model="rule.operator" class="cpp__select cpp__rule-op">
              <option v-for="op in RULE_OPS" :key="op.v" :value="op.v">
                {{ op.label }}
              </option>
            </select>
            <input
              v-model="rule.value"
              class="cpp__input cpp__rule-val"
              placeholder="valeur"
            />
            <button class="cpp__rule-rm" @click="removeRule(i)">
              <i class="pi pi-times" />
            </button>
          </div>
        </div>

        <!-- Erreur -->
        <div v-if="error" class="cpp__error">
          <i class="pi pi-exclamation-triangle" />
          <pre>{{ error }}</pre>
        </div>
      </div>

      <!-- ── Colonne latérale ── -->
      <div class="cpp__side">
        <!-- Statut -->
        <div class="cpp__card">
          <h2 class="cpp__card-title">
            <i class="pi pi-circle-fill" /> Statut
          </h2>
          <div class="cpp__status-opts">
            <label
              v-for="opt in [
                {
                  v: 'active',
                  label: 'Actif',
                  desc: 'Démarre immédiatement',
                  dot: '#10b981',
                },
                {
                  v: 'draft',
                  label: 'Brouillon',
                  desc: 'Non publié',
                  dot: '#9ca3af',
                },
                {
                  v: 'paused',
                  label: 'En pause',
                  desc: 'Désactivé temporairement',
                  dot: '#f59e0b',
                },
              ]"
              :key="opt.v"
              class="cpp__status-opt"
              :class="{ 'cpp__status-opt--on': form.status === opt.v }"
            >
              <input
                type="radio"
                :value="opt.v"
                v-model="form.status"
                class="cpp__radio"
              />
              <span class="cpp__status-dot" :style="{ background: opt.dot }" />
              <div>
                <span class="cpp__status-label">{{ opt.label }}</span>
                <span class="cpp__status-desc">{{ opt.desc }}</span>
              </div>
            </label>
          </div>
        </div>

        <!-- Planification -->
        <div class="cpp__card">
          <h2 class="cpp__card-title">
            <i class="pi pi-calendar" /> Planification
          </h2>
          <div class="cpp__fields cpp__fields--1col">
            <div class="cpp__field">
              <label class="cpp__label">Date de début</label>
              <input
                v-model="form.startDate"
                type="datetime-local"
                class="cpp__input"
              />
            </div>
            <div class="cpp__field">
              <label class="cpp__label">Date de fin</label>
              <input
                v-model="form.endDate"
                type="datetime-local"
                class="cpp__input"
              />
            </div>
          </div>
        </div>

        <!-- Limites -->
        <div class="cpp__card">
          <h2 class="cpp__card-title">
            <i class="pi pi-lock" /> Limites d'utilisation
          </h2>
          <div class="cpp__fields cpp__fields--1col">
            <div class="cpp__field">
              <label class="cpp__label">Limite totale</label>
              <input
                v-model.number="form.usageLimit"
                type="number"
                min="0"
                class="cpp__input"
                placeholder="0 = illimité"
              />
            </div>
            <div class="cpp__field">
              <label class="cpp__label">Limite par client</label>
              <input
                v-model.number="form.usageLimitPerCustomer"
                type="number"
                min="0"
                class="cpp__input"
                placeholder="0 = illimité"
              />
            </div>
          </div>
        </div>

        <!-- Avancé -->
        <div class="cpp__card">
          <h2 class="cpp__card-title"><i class="pi pi-cog" /> Avancé</h2>
          <div class="cpp__fields cpp__fields--1col">
            <div class="cpp__field">
              <label class="cpp__label">Priorité</label>
              <input
                v-model.number="form.priority"
                type="number"
                min="0"
                class="cpp__input"
                placeholder="0 = défaut"
              />
              <span class="cpp__hint">Plus élevé = priorité supérieure</span>
            </div>
            <label class="cpp__check-row">
              <input
                type="checkbox"
                v-model="form.stackable"
                class="cpp__checkbox"
              />
              <div>
                <span class="cpp__check-label">Cumulable</span>
                <span class="cpp__check-desc"
                  >Combinable avec d'autres promotions</span
                >
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cpp__page {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 70px);
  background: var(--nxt-bg, #f4f6f8);
}
[data-theme='dark'] .cpp__page {
  background: var(--nxt-bg, #0e1012);
}

/* Header */
.cpp__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.125rem 2rem;
  background: var(--nxt-surface, #fff);
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  position: sticky;
  top: 0;
  z-index: 50;
}
[data-theme='dark'] .cpp__header {
  background: var(--nxt-surface-low, #141618);
  border-color: rgba(255, 255, 255, 0.08);
}
.cpp__back {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-on-surface);
  transition: background 0.15s;
}
.cpp__back:hover {
  background: var(--nxt-surface-container, rgba(0, 0, 0, 0.05));
}
.cpp__header-info {
  flex: 1;
}
.cpp__title {
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
  margin: 0;
}
.cpp__sub {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  margin: 0.1rem 0 0;
  font-family: monospace;
}
.cpp__header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

/* Buttons */
.cpp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.625rem 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
}
.cpp__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.cpp__btn--ghost {
  background: transparent;
  border-color: var(--nxt-outline-variant, rgba(0, 0, 0, 0.15));
  color: var(--nxt-on-surface);
}
.cpp__btn--ghost:hover:not(:disabled) {
  background: var(--nxt-surface-container, rgba(0, 0, 0, 0.04));
}
.cpp__btn--primary {
  background: var(--nxt-on-surface, #111);
  color: var(--nxt-surface, #fff);
}
.cpp__btn--primary:hover:not(:disabled) {
  opacity: 0.82;
}
[data-theme='dark'] .cpp__btn--primary {
  background: var(--nxt-on-surface, #e2e4e5);
  color: #141618;
}

/* Layout */
.cpp__body {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 1.5rem;
  padding: 2rem;
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
}
@media (max-width: 900px) {
  .cpp__body {
    grid-template-columns: 1fr;
  }
}

/* Card */
.cpp__card {
  background: var(--nxt-surface, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
}
[data-theme='dark'] .cpp__card {
  background: var(--nxt-surface-low, #1a1c1e);
  border-color: rgba(255, 255, 255, 0.07);
}
.cpp__card-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}
.cpp__card-title {
  font-size: 0.75rem;
  font-weight: 800;
  color: var(--nxt-on-surface-variant);
  margin: 0 0 1rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.cpp__card-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  border-radius: 9999px;
  padding: 0 0.3rem;
  background: var(--nxt-on-surface, #111);
  color: var(--nxt-surface, #fff);
  font-size: 0.6875rem;
  font-weight: 800;
}
[data-theme='dark'] .cpp__card-badge {
  background: #e2e4e5;
  color: #111;
}
.cpp__card-hint {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  margin: -0.5rem 0 0.875rem;
}

/* Fields */
.cpp__fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.875rem;
}
.cpp__fields--1col {
  grid-template-columns: 1fr;
}
.cpp__field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.cpp__field--full {
  grid-column: 1/-1;
}
.cpp__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}
.cpp__req {
  color: #dc2626;
}
.cpp__hint {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
}

.cpp__input {
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-family: inherit;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  background: var(--nxt-surface-container, rgba(0, 0, 0, 0.02));
  color: var(--nxt-on-surface);
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.15s;
}
.cpp__input:focus {
  outline: none;
  border-color: var(--nxt-on-surface, #111);
}
[data-theme='dark'] .cpp__input {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}
.cpp__textarea {
  resize: vertical;
  min-height: 4rem;
}
.cpp__select {
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-family: inherit;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  background: var(--nxt-surface-container, rgba(0, 0, 0, 0.02));
  color: var(--nxt-on-surface);
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
}
[data-theme='dark'] .cpp__select {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}
.cpp__input-wrap {
  position: relative;
}
.cpp__input--sym {
  padding-right: 2.5rem;
}
.cpp__sym {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface-variant);
  pointer-events: none;
}
.cpp__input--code {
  font-family: monospace;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

/* Trigger */
.cpp__trigger-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
}
@media (max-width: 600px) {
  .cpp__trigger-grid {
    grid-template-columns: 1fr;
  }
}
.cpp__trigger-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
  padding: 0.875rem 1rem;
  border-radius: 0.875rem;
  text-align: left;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}
.cpp__trigger-btn:hover {
  border-color: rgba(0, 0, 0, 0.22);
  background: var(--nxt-surface-container, rgba(0, 0, 0, 0.03));
}
[data-theme='dark'] .cpp__trigger-btn:hover {
  border-color: rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.04);
}
.cpp__trigger-btn--on {
  border-color: var(--nxt-on-surface, #111);
  background: var(--nxt-on-surface, #111);
}
[data-theme='dark'] .cpp__trigger-btn--on {
  border-color: #e2e4e5;
  background: #e2e4e5;
}
.cpp__trigger-ico {
  font-size: 1.125rem;
  color: var(--nxt-on-surface-variant);
  margin-bottom: 0.25rem;
}
.cpp__trigger-btn--on .cpp__trigger-ico {
  color: #fff;
}
[data-theme='dark'] .cpp__trigger-btn--on .cpp__trigger-ico {
  color: #141618;
}
.cpp__trigger-label {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cpp__trigger-btn--on .cpp__trigger-label {
  color: #fff;
}
[data-theme='dark'] .cpp__trigger-btn--on .cpp__trigger-label {
  color: #141618;
}
.cpp__trigger-desc {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.4;
}
.cpp__trigger-btn--on .cpp__trigger-desc {
  color: rgba(255, 255, 255, 0.65);
}
[data-theme='dark'] .cpp__trigger-btn--on .cpp__trigger-desc {
  color: rgba(0, 0, 0, 0.55);
}

/* Pickers */
.cpp__search-wrap {
  position: relative;
  margin-bottom: 0.75rem;
}
.cpp__search-ico {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  pointer-events: none;
}
.cpp__search-input {
  padding-left: 2.5rem;
}

.cpp__picker-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: 220px;
  overflow-y: auto;
}
.cpp__picker-list--cats {
  max-height: 160px;
}
.cpp__picker-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.625rem;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.12s;
}
.cpp__picker-row:hover {
  background: var(--nxt-surface-container, rgba(0, 0, 0, 0.04));
}
.cpp__picker-row--on {
  border-color: var(--nxt-on-surface, #111);
  background: rgba(0, 0, 0, 0.03);
}
[data-theme='dark'] .cpp__picker-row--on {
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.05);
}
.cpp__picker-ico {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
}
[data-theme='dark'] .cpp__picker-ico {
  background: rgba(255, 255, 255, 0.07);
}
.cpp__picker-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cpp__picker-name {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cpp__picker-price {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  flex-shrink: 0;
}
.cpp__picker-check {
  font-size: 1rem;
  flex-shrink: 0;
  color: var(--nxt-on-surface-variant);
}
.cpp__picker-row--on .cpp__picker-check {
  color: var(--nxt-on-surface, #111);
}
.cpp__picker-empty {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  padding: 0.5rem 0.75rem;
}

/* Rules */
.cpp__add-rule-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  border: 1px dashed var(--nxt-outline-variant, rgba(0, 0, 0, 0.15));
  background: transparent;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition: all 0.15s;
}
.cpp__add-rule-btn:hover {
  border-color: var(--nxt-on-surface);
  color: var(--nxt-on-surface);
}
.cpp__rules-empty {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  padding: 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.cpp__rule-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
}
.cpp__rule-select {
  flex: 2;
  min-width: 0;
}
.cpp__rule-op {
  flex: 1;
  min-width: 0;
}
.cpp__rule-val {
  flex: 1;
  min-width: 0;
}
.cpp__rule-rm {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: none;
  background: transparent;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
}
.cpp__rule-rm:hover {
  background: #fee2e2;
  color: #dc2626;
}

/* Status */
.cpp__status-opts {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.cpp__status-opt {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  cursor: pointer;
  transition: all 0.15s;
}
.cpp__status-opt:hover {
  border-color: rgba(0, 0, 0, 0.2);
}
.cpp__status-opt--on {
  border-color: var(--nxt-on-surface, #111);
  background: rgba(0, 0, 0, 0.02);
}
[data-theme='dark'] .cpp__status-opt--on {
  border-color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.04);
}
.cpp__radio {
  accent-color: var(--nxt-on-surface, #111);
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  cursor: pointer;
}
.cpp__status-dot {
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 50%;
  flex-shrink: 0;
}
.cpp__status-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cpp__status-desc {
  display: block;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
}

/* Check */
.cpp__check-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  transition: border-color 0.15s;
}
.cpp__check-row:hover {
  border-color: rgba(0, 0, 0, 0.2);
}
.cpp__checkbox {
  width: 1rem;
  height: 1rem;
  accent-color: var(--nxt-on-surface, #111);
  cursor: pointer;
  margin-top: 0.125rem;
  flex-shrink: 0;
}
.cpp__check-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cpp__check-desc {
  display: block;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
}

/* Error */
.cpp__error {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 0.875rem;
  background: #fee2e2;
  border: 1px solid #fca5a5;
  color: #dc2626;
  font-size: 0.875rem;
}
.cpp__error pre {
  margin: 0;
  font-family: inherit;
  white-space: pre-wrap;
}
</style>
